import { useEffect, useCallback, useRef, useState } from 'react';
import { equals } from 'ramda';

const responseToJson = (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  throw new Error(
    `Things went sideways: statusCode: ${response.status}, statusText: ${response.statusText}`
  );
};

type TUseFetchOptions = RequestInit & {
  /**
   * Skip automatically executing the fetch on hook call. Instead you need to
   * manually call the returned function.
   */
  skip?: boolean;
};

type TFetchState<T> =
  | {
      loading: true;
    }
  | {
      loading: false;
      data: T;
      error: undefined;
    }
  | {
      loading: false;
      error: Error;
      data: undefined;
    };

type TUseFetchResult<T> = [TFetchState<T>, (urlOverride?: string) => void];

export const useFetch = <T>(
  url: string,
  options?: TUseFetchOptions
): TUseFetchResult<T> => {
  const opts = useRef<typeof options>();
  if (!equals(opts.current, options)) {
    opts.current = options;
  }

  const fetchOnMount = !!(opts.current && !opts.current.skip);

  const [state, setState] = useState({
    loading: fetchOnMount,
  } as TFetchState<T>); // Typecast because boolean !== true | false

  const doFetch = useCallback(
    (urlOverride?: string) => {
      setState({ loading: true });
      fetch(urlOverride || url, opts.current)
        .then(responseToJson)
        .then(data => ({ loading: false as false, data, error: undefined })) // GJ TS
        .catch(error => ({ loading: false as false, error, data: undefined }))
        .then(setState);
    },
    [url]
  );

  useEffect(() => {
    if (fetchOnMount) {
      doFetch();
    }
  }, [doFetch, fetchOnMount]);

  return [state, doFetch];
};

export const useGet = <T>(url: string, options?: TUseFetchOptions) =>
  useFetch<T>(url, { ...options, method: 'get' });

export const usePost = <T>(url: string, options?: TUseFetchOptions) =>
  useFetch<T>(url, { ...options, method: 'post' });
